import { useEnvironment } from '@webng/react-app-common';
import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';

type OpenListener =  ((ws: WebSocket) => void)
type CloseListener =  (() => void)
type MessageListener =  ((message: any) => void)
type ErrorListener =  ((event: Event) => void)
type VisibilityChangeListener =  ((hidden: boolean) => void)
type NetworkChangeListener =  ((online: boolean) => void)

type EventListenerManagerFn = ((type: 'open', listener: OpenListener) => void) & ((type: 'close', listener: CloseListener) => void) & ((type: 'message', listener: MessageListener) => void)  & ((type: 'error', listener: ErrorListener) => void) & ((type: 'visibilitychange', listener: VisibilityChangeListener) => void) & ((type: 'networkchange', listener: NetworkChangeListener) => void)

export interface ChangeBridgeContextValue {
  ws: WebSocket | null;
  liveblogLocalId?: string;
  hasErrored: boolean;
  isConnected: boolean;
  addEventListener: EventListenerManagerFn;
  removeEventListener: EventListenerManagerFn;
  setLiveblogLocalId: (liveblogLocalId: string) => void;
}

function createHeartbeatTimeoutFn(ws: WebSocket) {
  return () => {
    console.error("❌ No heartbeat received from server, closing connection");
    ws.close();
  }
}

function useChangeBridge(liveblogLocalId?: string): Omit<ChangeBridgeContextValue, "setLiveblogLocalId"> {

  const wsRef = useRef<WebSocket | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const realRetryCount = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const [hasErrored, setHasErrored] = useState(false);
  const env = useEnvironment();
  const abortControllerRef = useRef<AbortController | null>(null);

  const listenersRef = useRef<{
    'open': OpenListener[],
    'close': CloseListener[],
    'message': MessageListener[],
    'error': ErrorListener[],
    'visibilitychange': VisibilityChangeListener[],
    'networkchange': NetworkChangeListener[],
  }>({
    open: [],
    close: [],
    message: [],
    error: [],
    visibilitychange: [],
    networkchange: [],
  });

  const addEventListener: EventListenerManagerFn = useCallback((type, listener) => {
    if (listenersRef.current[type]) {
      listenersRef.current[type].push(listener as any);
    }
  }, [])

  const removeEventListener: EventListenerManagerFn = useCallback((type, listener) => {
    if (listenersRef.current[type]) {
      const index = listenersRef.current[type].indexOf(listener as any);
      if (index !== -1) {
        listenersRef.current[type].splice(index, 1);
      }
    }
  }, [])

  const onVisibilityChange = useCallback(() => {
    if (document.hidden) {
      setHasErrored(true);
      setRetryCount(0);
      realRetryCount.current = 0;
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounted");
        wsRef.current = null;
      }
    } else {
      setHasErrored(false);
    }

    listenersRef.current.visibilitychange.forEach((listener) => listener(document.hidden));
  }, [listenersRef, setHasErrored, setRetryCount, wsRef]);

  const onNetworkChange = useCallback(() => {
    if (navigator.onLine) {
      setHasErrored(false);
    } else {
      setHasErrored(true);
      setRetryCount(0);
      realRetryCount.current = 0;
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounted");
        wsRef.current = null;
      }
    }

    listenersRef.current.networkchange.forEach((listener) => listener(navigator.onLine));
  }, [listenersRef, setHasErrored, setRetryCount, wsRef]);

  useEffect(() => {
    if (!liveblogLocalId) return;

    if(abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    if (hasErrored) return;

    try {
      const apiHost = env.apiHost ? env.apiHost.replace("https://", "wss://") : "wss://staging.tickaroo.com"
      const ws = new WebSocket(`${apiHost}/changebridge/${liveblogLocalId}`);

      if(signal.aborted) {
        ws.close();
        return;
      }

      const heartbeat = setInterval(() => {
        if(signal.aborted) {
          clearInterval(heartbeat);
          return;
        }
        ws.send(JSON.stringify({ type: "cb_ping", ts: Date.now() }));
      }, 20000)

      const heartbeatTimeoutFn = createHeartbeatTimeoutFn(ws);

      let heartbeatTimeout = setTimeout(heartbeatTimeoutFn, 60000)

      ws.addEventListener("open", () => {
        if(signal.aborted) {
          ws.close();
          return;
        }
        realRetryCount.current = 0;
        setIsConnected(true);

        listenersRef.current.open.forEach((listener) => listener(ws));
      });

      ws.addEventListener("message", (event) => {
        if(signal.aborted) {
          return;
        }
        const message = JSON.parse(event.data);
        if(message.type.startsWith("cb_")) {
          if (message.type === "cb_pong") {
            clearTimeout(heartbeatTimeout);
            heartbeatTimeout = setTimeout(heartbeatTimeoutFn, 60000)
            return;
          } else {
            return;
          }
        }
      
        listenersRef.current.message.forEach((listener) => listener(message));
      });

      ws.addEventListener("close", (event) => {
        setIsConnected(false);
        if (event.code !== 1000 && event.reason !== "Component unmounted") {
          console.error(`❌ WebSocket error: ${event.code} ${event.reason}`);
          if (realRetryCount.current >= 5) {
            console.error("❌ Too many retries, giving up");
            clearInterval(heartbeat);
            clearTimeout(heartbeatTimeout);
            setHasErrored(true);
          }
          setTimeout(() => {
            if(signal.aborted) {
              return;
            }
            setRetryCount((prev) => prev + 1);
            realRetryCount.current += 1;
          }, 5000);
        }

        listenersRef.current.close.forEach((listener) => listener());
      });

      ws.addEventListener("error", (err) => {
        console.error("⚠️ WebSocket error:", err);
        setIsConnected(false);

        listenersRef.current.error.forEach((listener) => listener(err));
      });

      wsRef.current = ws;
      if(signal.aborted) {
        ws.close();
        return;
      }

      // Cleanup on unmount
      return () => {
        ws.close(1000, "Component unmounted");
        clearInterval(heartbeat);
        clearTimeout(heartbeatTimeout);

        if(abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      };
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      // notifyErrorRaw(error) TODO: connect to analytics api
      setHasErrored(true);

      // Retry logic for WebSocket creation errors
      if (realRetryCount.current < 5) {
        setTimeout(() => {
          if(signal.aborted) {
            return;
          }
          setRetryCount((prev) => prev + 1);
          setHasErrored(false);
          realRetryCount.current += 1;
        }, 5000);
      }
    }
  }, [retryCount, liveblogLocalId, hasErrored, setHasErrored, env]);

  useEffect(() => {
    document.addEventListener("visibilitychange", onVisibilityChange, false);
    window.addEventListener("online", onNetworkChange, false);
    window.addEventListener("offline", onNetworkChange, false);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange, false);
      window.removeEventListener("online", onNetworkChange, false);
      window.removeEventListener("offline", onNetworkChange, false);
    };
  }, [onVisibilityChange, onNetworkChange]);


  return {
    ws: wsRef.current,
    hasErrored,
    isConnected,
    addEventListener,
    removeEventListener,
    liveblogLocalId
  };
}

const ChangeBridgeContext = createContext<ChangeBridgeContextValue | null>(null);

export const useChangeBridgeContext = () => {
  return useContext(ChangeBridgeContext);
}

export const ChangeBridgeProvider: React.FC<{ children: React.ReactNode, liveblogLocalId?: string, setLiveblogLocalId: (liveblogLocalId: string) => void }> = ({ children, liveblogLocalId, setLiveblogLocalId }) => {
  const changeBridgeValue = useChangeBridge(liveblogLocalId);

  return (
    <ChangeBridgeContext.Provider value={{...changeBridgeValue, setLiveblogLocalId}}>
      {children}
    </ChangeBridgeContext.Provider>
  );
};
